import SyncDbIcon from '@/assets/icons/SyncDbIcon';
import Button from '@/components/Common/Button';
import DataTable from '@/components/Common/DataTable';
import { Box, Typography, useTheme, Chip, Stack, Drawer, IconButton, Pagination as MUIPagination } from '@mui/material';
import { MdClose } from 'react-icons/md';
import { useState } from 'react';
import { IoIosPlay } from 'react-icons/io';
import { ColumnDef } from '@tanstack/react-table';
import { MdInfoOutline } from 'react-icons/md';
import QuickActionIcon from '@/assets/icons/QuickActionIcon';
import AutomationIcon from '@/assets/icons/AutomationIcon';
import syncing from '@/assets/syncing.gif';
import { GoClock } from 'react-icons/go';
import Select from '@/components/Common/Select';
import { useParams } from 'react-router-dom';
import {
  useUpdateIntegration,
  useStartInitialSync,
  useManualSync,
  useGetSynchronizerLogs,
  useGetSynchronizedItems,
  useGetIntegration
} from '@/apis/business/setting/integrations/hook';
import Pagination, { usePagination } from '@/components/Common/Pagination';
import { titleCase } from '@/utils/helperFunctions';

export default function SyncTab({
  selectedIntegrationData,
  control,
  setValue,
  totalSyncedContacts
}: {
  selectedIntegrationData: any;
  control: any;
  setValue: any;
  totalSyncedContacts: number;
}) {
  const theme = useTheme();
  const [syncInProgress, setSyncInProgress] = useState(false); // true after POST success until we have logs or reload
  const [apiLogsDrawerOpen, setApiLogsDrawerOpen] = useState(false);
  const [selectedSynchronizerLogId, setSelectedSynchronizerLogId] = useState<number | null>(null);
  const [apiLogsPage, setApiLogsPage] = useState(1);
  const {
    currentPage: syncLogsPage,
    perPage: syncLogsPerPage,
    setPage: setSyncLogsPage,
    setPerPage: setSyncLogsPerPage
  } = usePagination();

  const { mutateAsync: updateIntegration } = useUpdateIntegration();
  const { mutateAsync: startInitialSync, isPending: isInitialSyncStarting } = useStartInitialSync();
  const { mutateAsync: manualSync, isPending: isManualSyncRunning } = useManualSync();
  const { integrationId } = useParams();
  const { data: logsResponse, isLoading: isLoadingLogs } = useGetSynchronizerLogs({
    integration_id: integrationId ?? null,
    page: syncLogsPage,
    per_page: syncLogsPerPage
  });
  const { data: integrationData, isLoading: loadingIntegration } = useGetIntegration(Number(integrationId));
  const { data: synchronizedItemsResponse, isLoading: isLoadingSynchronizedItems } = useGetSynchronizedItems(
    selectedSynchronizerLogId,
    apiLogsPage
  );

  const hasLogs = (logsResponse?.data?.length ?? 0) > 0;

  // 0: no logs + not in progress → Start Initial Sync
  // 1: user just started sync (POST success) and no logs yet → Sync In Progress
  // 2: we have logs (from GET) → show table
  const viewState = syncInProgress && !hasLogs ? 1 : hasLogs ? 2 : 0;

  if (integrationId && isLoadingLogs) {
    return (
      <Box
        sx={{
          border: '1px dashed #e5e7eb',
          borderRadius: 3,
          p: 6,
          textAlign: 'center',
          backgroundColor: '#fff'
        }}
      >
        <Typography color='text.secondary'>Loading sync status…</Typography>
      </Box>
    );
  }

  if (viewState === 0) {
    return (
      <Box
        sx={{
          border: '1px dashed #e5e7eb',
          borderRadius: 3,
          p: 6,
          textAlign: 'center',
          backgroundColor: '#fff'
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.light,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2
          }}
        >
          <SyncDbIcon />
        </Box>

        <Typography fontSize={16} mb={1} fontWeight={500}>
          Sync Your Records
        </Typography>

        <Typography fontSize={14} color='text.secondary' mb={3}>
          Start the initial sync to copy all existing contacts and associated records from{' '}
          {selectedIntegrationData?.name}.
        </Typography>

        <Button
          startIcon={<IoIosPlay size={16} />}
          onClick={async () => {
            if (!integrationId) return;
            try {
              await startInitialSync();
              setSyncInProgress(true);
            } catch {
              // Error thrown by http (e.g. 404 Integration not found); could show toast here
            }
          }}
          disabled={isInitialSyncStarting || !integrationId}
        >
          {isInitialSyncStarting ? 'Starting…' : 'Start Initial Sync'}
        </Button>
      </Box>
    );
  }

  if (viewState === 1) {
    return (
      <Box
        sx={{
          border: '1px dashed #e5e7eb',
          borderRadius: 3,

          p: 6,
          textAlign: 'center',
          backgroundColor: '#fff'
        }}
      >
        <img src={syncing} alt='loading' />
        <Typography fontSize={16} mb={1} fontWeight={500}>
          Sync In Progress
        </Typography>
        <Typography fontSize={14} color='text.secondary' mb={3}>
          We are importing your records from {selectedIntegrationData?.name ?? 'your source'}. Please do not close this
          page.
        </Typography>
        <Stack direction='row' justifyContent='center' alignItems='center' gap={1}>
          <GoClock size={14} color='#777777' />
          <Typography color='#777777' fontSize={14}>
            Sync completion time depends upon your data size
          </Typography>
        </Stack>
      </Box>
    );
  }

  const tableData = (logsResponse?.data ?? []).map((row) => ({
    id: row.id,
    date_time: formatDateTime(row.started_at),
    status: row.status,
    duration: row.total_duration,
    api_hits: row.api_hits,
    total: row.total_synced_count,
    new: row.metadata?.total_created_count,
    updated: row.metadata?.total_updated_count,
    failed: row.total_failed_count
  }));

  function formatDateTime(iso: string) {
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return iso;
    }
  }

  // ================= COLUMNS =================
  const columns: ColumnDef<any>[] = [
    {
      header: 'DATE & TIME',
      accessorKey: 'date_time'
    },
    {
      header: 'STATUS',
      accessorKey: 'status',
      cell: ({ row }) => {
        const status = row.original.status;

        const colorMap: Record<string, { text: string; bg: string }> = {
          started: {
            text: theme.palette.info.dark ?? theme.palette.info.contrastText ?? '#0d47a1',
            bg: theme.palette.info.light
          },
          running: { text: theme.palette.primary.main, bg: theme.palette.primary.light },
          completed: { text: theme.palette.success.main, bg: theme.palette.success.light },
          failed: { text: theme.palette.error.main, bg: theme.palette.error.light },
          partial: { text: theme.palette.warning.main, bg: theme.palette.warning.light }
        };

        const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : status;

        return (
          <Chip
            label={label}
            size='small'
            sx={{ fontWeight: 500, borderRadius: '10px', color: colorMap[status]?.text, bgcolor: colorMap[status]?.bg }}
          />
        );
      }
    },
    {
      header: 'DURATION',
      accessorKey: 'duration'
    },
    {
      header: 'API HITS',
      accessorKey: 'api_hits',
      cell: ({ row }) => (
        <Box
          display='flex'
          alignItems='center'
          gap={0.5}
          sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedSynchronizerLogId(row.original.id);
            setApiLogsPage(1);
            setApiLogsDrawerOpen(true);
          }}
        >
          {row.original.api_hits}
          <MdInfoOutline size={16} />
        </Box>
      )
    },
    {
      header: 'TOTAL',
      accessorKey: 'total'
    },
    {
      header: 'NEW',
      accessorKey: 'new',
      cell: ({ row }) => <Typography color='success.main'>{row.original.new}</Typography>
    },
    {
      header: 'UPDATED',
      accessorKey: 'updated',
      cell: ({ row }) => <Typography color='primary.main'>{row.original.updated}</Typography>
    },
    {
      header: 'FAILED',
      accessorKey: 'failed',
      cell: ({ row }) => (
        <Typography color='error.main'>{row.original.failed === 0 ? '-' : row.original.failed}</Typography>
      )
    }
  ];

  const getLabelValue = (label: any) => {
    switch (label) {
      case 'every_hour':
        return 'hourly';
      case 'every_6_hour':
        return 'after_six_hours';
      case 'daily':
        return 'daily';
      case 'monthly':
        return 'monthly';
      case 'weekly':
        return 'weekly';
      default:
        return 'undefined';
    }
  };

  return (
    <Box>
      {/* Top Section */}
      <Box
        display='flex'
        flexDirection='row'
        bgcolor='#fff'
        borderRadius='8px'
        padding='16px'
        gap={2}
        mb={4}
        alignItems='center'
        border='1px solid #ECECEC'
      >
        <Box
          display='flex'
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'
          bgcolor={theme.palette.primary.light}
          width='100%'
          padding='24px'
          gap={2}
          borderRadius='10px'
        >
          <Stack direction='row' gap={1.5} alignItems='center'>
            <QuickActionIcon />
            <Stack>
              <Typography fontWeight={400} fontSize={12} lineHeight={1}>
                Quick Action
              </Typography>
              <Typography fontWeight={500} fontSize={16}>
                Sync Now
              </Typography>
            </Stack>
          </Stack>
          <Button
            startIcon={<IoIosPlay size={16} />}
            sx={{ minHeight: 40 }}
            onClick={() => integrationId && manualSync()}
            disabled={!integrationId || isManualSyncRunning}
          >
            {isManualSyncRunning ? 'Running…' : 'Run Sync Now'}
          </Button>
        </Box>
        <Box
          display='flex'
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          border='1px solid #ECECEC'
          width='100%'
          padding='24px'
          gap={2}
          borderRadius='10px'
        >
          <Stack direction='row' gap={1.5} alignItems='center'>
            <AutomationIcon />
            <Stack>
              <Typography fontWeight={400} fontSize={12} lineHeight={1}>
                Automation
              </Typography>
              <Typography fontWeight={500} fontSize={16} noWrap>
                Sync Schedule
              </Typography>
            </Stack>
          </Stack>

          <Select
            label=''
            control={control}
            disabled={loadingIntegration}
            name='frequency'
            sx={{ height: '45px' }}
            onChange={async ({ target }) => {
              setValue('frequency', target.value);
              await updateIntegration({ integration: { frequency: target?.value } } as any);
            }}
            options={
              integrationData?.default_frequency?.map((val: string) => ({
                label: titleCase(val),
                value: getLabelValue(val)
              })) ?? []
            }
          />
        </Box>
      </Box>

      <Box bgcolor='#fff' padding='16px' borderRadius='8px'>
        <Stack direction='row' justifyContent='space-between'>
          <Typography fontSize={16} fontWeight={500} mb={2}>
            Sync Logs
          </Typography>
          <Typography color='secondary' fontSize={14}>
            Total Synced - {totalSyncedContacts ?? 'Unavailable'}
          </Typography>
        </Stack>

        <DataTable
          data={tableData}
          columns={columns}
          isLoading={isLoadingLogs}
          containerStyles={{ borderRadius: '4px' }}
        />
        <Pagination
          currentPage={syncLogsPage}
          totalPages={logsResponse?.meta?.total_pages}
          totalCount={logsResponse?.meta?.total_count ?? 0}
          perPage={syncLogsPerPage}
          onChange={setSyncLogsPage}
          onPerPageChange={setSyncLogsPerPage}
        />
      </Box>

      <Drawer
        anchor='right'
        open={apiLogsDrawerOpen}
        onClose={() => {
          setApiLogsDrawerOpen(false);
          setSelectedSynchronizerLogId(null);
          setApiLogsPage(1);
        }}
        slotProps={{
          backdrop: { invisible: false },
          paper: {
            sx: { width: { xs: '100%', sm: 480 }, maxWidth: '100%' }
          }
        }}
      >
        <Box height='100%' display='flex' flexDirection='column' bgcolor='background.paper'>
          {/* Header */}
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            px={2}
            py={2}
            borderBottom='1px solid'
            borderColor='divider'
          >
            <Box>
              <Typography fontSize={18} fontWeight={600}>
                API Request Logs
              </Typography>
              <Typography fontSize={13} color='text.secondary'>
                {isLoadingSynchronizedItems
                  ? 'Loading…'
                  : `${synchronizedItemsResponse?.meta?.total_count ?? 0} requests made during this sync`}
              </Typography>
            </Box>
            <IconButton
              size='small'
              onClick={() => {
                setApiLogsDrawerOpen(false);
                setSelectedSynchronizerLogId(null);
                setApiLogsPage(1);
              }}
              aria-label='Close'
            >
              <MdClose size={22} />
            </IconButton>
          </Box>

          {/* Content */}
          <Box flex={1} overflow='auto' px={2} py={2}>
            {isLoadingSynchronizedItems ? (
              <Typography color='text.secondary' fontSize={14}>
                Loading…
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {(synchronizedItemsResponse?.data ?? []).map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'grey.50'
                    }}
                  >
                    <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap' mb={0.5}>
                      <Chip
                        label={item.status ?? '—'}
                        size='small'
                        sx={{
                          fontWeight: 600,
                          bgcolor: theme.palette.success.main,
                          color: theme.palette.success.contrastText ?? '#fff',
                          height: 22
                        }}
                      />
                      <Typography component='span' fontSize={13} fontWeight={500}>
                        GET
                      </Typography>
                      <Typography fontSize={12} color='text.secondary' sx={{ wordBreak: 'break-all' }}>
                        {item.request_endpoint}
                      </Typography>
                    </Stack>
                    {item.error_message ? (
                      <Typography fontSize={13} color='error'>
                        {item.error_message}
                      </Typography>
                    ) : (
                      <Typography fontSize={13} color='text.secondary'>
                        Fetched {item.fetched_items_count ?? item.payload_snapshot?.synced_count ?? 0} contact records
                        successfully.
                      </Typography>
                    )}
                    <Typography fontSize={12} color='text.secondary' mt={0.5}>
                      {item.api_duration_in_ms}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {/* Pagination */}
          {synchronizedItemsResponse?.meta && synchronizedItemsResponse.meta.total_pages > 1 && (
            <Box px={2} py={2} borderTop='1px solid' borderColor='divider' display='flex' justifyContent='center'>
              <MUIPagination
                count={synchronizedItemsResponse.meta.total_pages}
                page={apiLogsPage}
                onChange={(_, newPage) => setApiLogsPage(newPage)}
                color='primary'
                size='medium'
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
